import { useLayoutEffect, useRef, type InputHTMLAttributes } from 'react';
import { formatPhone, formatPhoneAsTyped } from '@peles/shared';

export interface PhoneInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  value: string;
  /** Called with the number as it now reads — laid out, never stripped to digits. */
  onChange: (value: string) => void;
}

const digitCount = (text: string) => text.replace(/\D/g, '').length;

/** Where the cursor goes in `text` to sit just after its `n`th digit. */
function positionAfterDigits(text: string, n: number): number {
  if (n <= 0) return text.startsWith('+') ? 1 : 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (/\d/.test(text[i]) && ++seen === n) return i + 1;
  }
  return text.length;
}

/**
 * The one phone field. A number is laid out as it is typed — `(305) 555-1234`,
 * `052-123-4567` (`formatPhoneAsTyped`) — with the cursor kept after the same
 * digit, so an edit in the middle does not jump to the end; leaving the field
 * settles it (`formatPhone`, which also tidies an extension). Backspacing over
 * a bracket or dash takes the digit before it, as a person expects. A number
 * the formatter cannot place is left as typed.
 */
export function PhoneInput({
  value,
  onChange,
  onBlur,
  autoComplete = 'tel',
  ...rest
}: PhoneInputProps) {
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const input = ref.current;
    if (caret.current === null || !input || document.activeElement !== input) return;
    input.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  return (
    <input
      {...rest}
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete={autoComplete}
      value={value}
      onChange={(e) => {
        let raw = e.target.value;
        let at = e.target.selectionStart ?? raw.length;
        // Only a separator went, and the formatter would put it straight back:
        // take the digit beside it instead (before it for a backspace, after it
        // for Delete).
        if (raw.length < value.length && formatPhoneAsTyped(raw) === value) {
          const forward = (e.nativeEvent as InputEvent).inputType === 'deleteContentForward';
          if (forward) {
            const next = raw.slice(at).search(/\d/);
            if (next >= 0) raw = raw.slice(0, at) + raw.slice(at + next + 1);
          } else {
            const last = raw.slice(0, at).search(/\d(?=\D*$)/);
            if (last >= 0) {
              raw = raw.slice(0, last) + raw.slice(at);
              at = last;
            }
          }
        }
        const digitsBefore = digitCount(raw.slice(0, at));
        const next = formatPhoneAsTyped(raw);
        // A number left as typed keeps the cursor where the browser put it.
        caret.current = next === raw ? at : positionAfterDigits(next, digitsBefore);
        onChange(next);
      }}
      onBlur={(e) => {
        const settled = formatPhone(value);
        if (settled !== value) onChange(settled);
        onBlur?.(e);
      }}
    />
  );
}
